/**
 * 한국어 콘텐츠. 모드 이름은 `ko.json` 카탈로그를 따릅니다. 문체는 해요체.
 *
 * 용어 한 가지만 인터페이스와 다릅니다. 카탈로그는 작품과 덧그린 부분을 모두
 * 「그림」이라고 부르는데, 짧은 버튼에서는 문맥이 받쳐 주지만 긴 글에서는
 * 「모든 그림이 이 그림 안에」 같은 문장이 나옵니다. 그래서 본문에서는 작품을
 * 「그림」, 덧그린 부분을 「수정」으로 갈라 씁니다. 「수정」은 화면의
 * `reveal.retouched`(수정본)와 같은 낱말입니다.
 */
import type { LanguageContent } from '../registry.js';

export const KO: LanguageContent = {
  home: {
    intro: [
      'Retusch는 2명에서 10명이 함께하는 온라인 그림 게임이에요. 브라우저에서 바로 돌아가고, 설치도 계정도 필요 없어요. 라운드마다 한 영역이 지워진 그림을 받아요. 그 영역을 다시 그려서 작품 속으로 사라지게 만들면 돼요. 그다음에는 입장이 바뀌어서, 다른 사람들의 수정을 찾아내야 해요.',
      '거의 모든 그림 게임은 가장 빨리 알아보게 만든 사람에게 상을 줘요. Retusch는 그 반대를 요구해요. 이기는 건 들키지 않는 거예요.',
    ],
    steps: [
      {
        title: '구멍 뚫린 그림',
        text: '한 영역이 지워진 그림을 받아요.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: '다시 그려요',
        text: '그 영역을 작품에 녹아들도록 다시 그려요.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: '찾기',
        text: '모두의 수정이 담긴 그림이 돌아와요. 남들보다 먼저 찾아보세요.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: '공개',
        text: '숨은 자리가 하나씩 드러나고, 점수가 갈려요.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: '다 같이 찾기',
        text: '모두가 같은 그림에, 각자 다른 영역을 그려요. 그다음 다 함께 동시에 찾기 시작해요. 하나의 수정은 가장 먼저 알아본 한 사람만 찾을 수 있어요. 기본 모드이고, 항상 무슨 일인가 벌어지는 모드예요.',
      },
      {
        name: '스트리밍',
        text: '방장은 그리지 않아요. 나머지 전원이 방장의 그림 속에 숨고, 방장은 혼자서 찾아요. 생방송을 위해 만든 모드로, 찾는 과정 자체가 볼거리가 돼요. 영원히 무료예요.',
      },
      {
        name: '각자 한 장',
        text: '한 사람에 작품 한 장. 딱 한 사람의 수정을, 단 한 번의 클릭으로 찾아요. 규칙은 가장 단순하고, 긴장감은 가장 커요.',
      },
      {
        name: '한 명만 그리기',
        text: '한 라운드에 한 명이 그리고, 나머지 전원이 뒤를 쫓아요. 역할은 돌아가고, 그린 사람은 자기 숨은 자리가 무너지는지 버티는지 지켜봐요.',
      },
      {
        name: '배틀로얄',
        text: '찾는 방식은 같지만, 가장 먼저 들킨 수정의 주인이 탈락해요. 마지막 한 명이 남을 때까지 이어지고, 점수는 한 점도 세지 않아요.',
      },
      {
        name: '콘테스트',
        text: '아무도 찾지 않아요. 모두가 같은 그림의 같은 영역을 다시 그리고, 각자 가장 마음에 드는 수정에 투표해요. 받은 표 하나가 1점이에요.',
      },
    ],
    faq: [
      {
        question: '몇 명이 필요한가요?',
        answer:
          '2명에서 10명까지예요. 혼자라면 자동 상대가 빈자리를 채워서 바로 시작할 수 있어요. 네 명을 넘어가면 다 같이 찾기가 가장 재미있어져요.',
      },
      {
        question: '무료인가요?',
        answer:
          '네. 바로 시작, 다 같이 찾기, 스트리밍 모드는 무료이고 시간 제한도 판수 제한도 없어요. 다른 모드도 지금은 무료예요.',
      },
      {
        question: '계정이 필요한가요?',
        answer:
          '아니요. 이름을 적고, 누르고, 놀면 돼요. 계정은 이름과 아바타를 다음 판까지 이어 주는 역할만 해요. 선택 사항이에요.',
      },
      {
        question: '휴대폰에서도 되나요?',
        answer:
          '돼요. 화면이 터치에 맞게 바뀌어서 도구 줄이 접히고, 손가락 아래에 돋보기가 뜨고, 두 번 두드리면 확대돼요. 그래도 마우스와 큰 화면이 더 정확해요.',
      },
      {
        question: '친구는 어떻게 초대하나요?',
        answer:
          '방을 하나 만들고, 초대 링크를 복사해서 원하는 곳에 붙여 넣으면 돼요. 친구들은 곧바로 자리에 앉아요. 계정도 설치도 필요 없어요.',
      },
      {
        question: '‘수정’이 뭔가요?',
        answer:
          '지워진 영역에 당신이 그려 넣는 것이에요. 잘되면 원래 그림의 일부로 보여서 아무도 못 찾아요. 어긋나면 한눈에 보여요.',
      },
    ],
    headings: {
      intro: '사라지는 것이 목표인 그림 게임',
      steps: '이렇게 해요',
      modes: '여섯 가지 모드',
      faq: '자주 묻는 질문',
      more: '더 알아보기',
    },
    links: {
      rules: '전체 규칙과 점수 읽기',
      modes: '모드 자세히 비교하기',
    },
  },

  howToPlay: {
    title: 'Retusch 하는 법',
    summary:
      'Retusch의 전체 규칙. 한 라운드의 흐름, 숨는 쪽과 찾는 쪽의 점수, 그리기 도구, 그리고 숨는 요령과 찾는 요령.',
    sections: [
      {
        heading: '세 문장으로 보는 규칙',
        blocks: [
          '한 영역이 지워진 그림을 받고, 그 영역을 눈에 띄지 않게 다시 그려요.',
          '그다음 다 같이 수정된 그림들을 보면서, 다시 그려진 영역을 찾아요.',
          '빨리 찾으면 점수가 되고, 내 숨은 자리가 버티는 동안에도 점수가 돼요.',
        ],
      },
      {
        heading: '한 라운드의 흐름',
        blocks: [
          '한 판은 같은 라운드를 이어 붙인 것이고, 기본은 다섯 라운드예요. 라운드마다 세 장면을 지나요.',
          {
            list: [
              '그리기. 내 그림과 내 영역을 받고, 기본 2분 동안 다시 그려요. 완료 버튼은 제출이에요. 모두가 제출하면 타이머를 기다리지 않고 다음으로 넘어가요.',
              '찾기. 수정이 들어간 그림이 돌아오고, 기본 30초 동안 찾아요. 다시 그려진 영역을 클릭하면 그 자리에서 모두에게 공개돼요.',
              '공개. 아무도 못 찾은 것까지 포함해 모든 숨은 자리에 테두리와 이름이 붙어요. 슬라이더로 수정본과 원래 작품을 견주어 볼 수 있어요.',
            ],
          },
        ],
      },
      {
        heading: '점수',
        blocks: [
          '두 개의 계기가 동시에 돌아가는데, 방향이 서로 반대예요. 찾는 쪽은 빨라야 하고, 그린 쪽은 자기 영역이 오래 버텨야 해요.',
          {
            list: [
              '찾는 쪽: 처음 3초 안에 찾으면 1000점, 거기서 고르게 줄어들어 단계가 끝날 때 100점이 돼요. 빗나가도, 못 찾아도 0점이에요.',
              '그린 쪽: 곧바로 들키면 100점뿐이고, 거기서 고르게 올라가 끝까지 버티면 800점이에요. 아무도 못 찾은 수정은 최고점인 800점이에요.',
            ],
          },
          '그린 사람에게는 공개 때 딱 한 번, 가장 빨리 찾은 사람을 기준으로 점수가 들어가요. 그래서 두 명이 찾든 여덟 명이 찾든 결과는 같아요. 가장 먼저 찾은 사람만 의미가 있어요.',
          '단계를 몇 초로 정하든 점수 방식은 그대로예요. 찾는 시간을 늘려도 양쪽 끝값은 그대로이고, 그 사이 기울기만 달라져요.',
          '한 라운드에 실수는 한 번뿐이에요. 어떤 수정에도 닿지 않은 첫 클릭으로 그 라운드의 탐색은 끝나요. 이 규칙이 없으면 얻어걸릴 때까지 화면을 마구 찍게 되니까요. 대신 두 가지는 그냥 넘어가요. 내 영역을 누른 경우와, 이미 찾힌 수정을 누른 경우예요.',
        ],
      },
      {
        heading: '그리기 도구',
        blocks: [
          {
            list: [
              '붓(B): 자주 쓰는 굵기 세 가지와, 정확히 맞추는 슬라이더. 1, 2, 3 키로 세 굵기를 고를 수 있어요.',
              '채우기(P): 평평한 면을 한 번에 메워요. 내 영역 밖으로는 절대 넘어가지 않아요.',
              '스포이트(I): 그림 어디서든 색을 집어요. Alt를 누르고 있는 동안만 스포이트가 되고, 떼면 원래 도구로 돌아와요.',
              '손(H): 화면을 옮겨요. 내 영역 바깥에서는 붓과 채우기도 똑같이 움직이니까, 도구를 바꿀 일이 거의 없어요.',
              '되돌리기(Ctrl+Z 또는 Cmd+Z): 획 단위로 서른 번까지 되돌려요. 다시 실행은 없어요.',
              '팔레트 끝의 바탕색: 내 영역의 처음 색이에요. 이 색으로 칠하는 것이 곧 지우기예요. 다만 다른 획과 똑같아서 되돌릴 수도 있고 위에 덧칠할 수도 있어요. 그래서 지우개라는 도구가 따로 없어요.',
            ],
          },
        ],
      },
      {
        heading: '숨기 위한 다섯 가지 요령',
        blocks: [
          {
            list: [
              '색은 짐작하지 말고 스포이트로 집으세요. 스포이트는 주변과 똑같은 색을 줘요. 조금만 어긋난 면은 멀리서도 보이고, 딱 맞는 면은 보이지 않게 돼요.',
              '내 영역을 가로지르는 것은 이어 주세요. 벽, 지평선, 한쪽에서 들어온 나뭇가지는 반대쪽 제자리로 나가야 해요. 끊긴 선은 익숙한 사람이 가장 먼저 찾는 단서예요.',
              '모서리부터 칠하세요. 가장 흔한 실수예요. 가운데는 공들였는데 네 모서리가 처음 바탕색 그대로면, 그림을 볼 것도 없이 사각형이 눈에 들어와요.',
              '큰 것에서 작은 것으로 가세요. 먼저 굵은 붓으로 큰 색 덩어리를 놓고, 그다음에 다듬어요. 반대로 하면 한쪽 구석에 시간을 다 쓰고 나머지가 비어요.',
              '주변과 같은 밀도로 그리세요. 빽빽한 자리에 있는 너무 깔끔한 영역은, 평평한 면에 있는 대충 그린 영역만큼 눈에 띄어요.',
            ],
          },
        ],
      },
      {
        heading: '찾기 위한 네 가지 요령',
        blocks: [
          {
            list: [
              '끊긴 선을 찾으세요. 가장자리, 전선, 머리카락만큼 어긋난 책상 모서리. 거의 언제나 거기예요.',
              '먼저 훑고, 그다음에 확대하세요. 전체 화면에서 어색하게 섞인 영역은 얼룩처럼 보여요. 확대는 확인용이지 탐색용이 아니에요.',
              '너무 매끈한 곳을 의심하세요. 결이 있는 자리 한가운데의 완전히 고른 면은, 대충 칠한 자국만큼이나 수상해요.',
              '아무 데나 찍지 마세요. 실수는 한 번뿐이에요. 확인하느라 3초를 쓰는 편이 라운드 하나를 잃는 것보다 나아요.',
            ],
          },
        ],
      },
      {
        heading: '모드에 따라서는요?',
        blocks: [
          '여기까지는 기본 모드인 다 같이 찾기 이야기예요. 다른 모드에서는 누가 그리는지, 누가 찾는지, 어떻게 이기는지가 달라져요. 각 모드의 자세한 설명은 모드 페이지에 있어요.',
        ],
      },
    ],
  },

  modes: {
    title: 'Retusch의 게임 모드',
    summary:
      'Retusch를 즐기는 여섯 가지 방법. 다 같이 찾기, 스트리밍, 각자 한 장, 한 명만 그리기, 배틀로얄, 콘테스트. 진행 방식과 권장 인원, 소요 시간.',
    sections: [
      {
        heading: '다 같이 찾기',
        blocks: [
          '기본 모드이고, 처음 시작하기에 좋은 모드예요.',
          '모두가 같은 그림에, 각자 다른 영역을 그려요. 수정이 다 모이면 작품이 한 장으로 돌아오고, 다 함께 동시에 찾기 시작해요. 하나의 수정은 가장 먼저 알아본 한 사람만 찾을 수 있어요. 찾히는 순간 모두에게 표시되고 대상에서 빠져요. 내 것은 절대 못 찾고, 아무도 알아채지 못한 숨은 자리는 주인에게 최고점을 안겨 줘요.',
          '인원이 많을 때 좋아요. 빈 시간이 없고, 모두가 동시에 찾고, 누군가의 발견이 곧바로 모두의 화면에 떠요.',
          '권장: 3명에서 10명.',
        ],
      },
      {
        heading: '스트리밍',
        blocks: [
          '‘한 명만 그리기’를 뒤집은 모드로, 생방송을 위해 만들었어요.',
          '방장을 뺀 모든 사람이 같은 그림에 그려요. 그다음 찾는 사람은 방장뿐이고, 시청자들은 그 모습을 지켜봐요. 방장은 점수를 받지 않고 순위에도 오르지 않아요. 승부는 숨는 사람들끼리 가리고, 방장은 판을 이끄는 역할이에요. 몇 번을 틀려도 괜찮아요. 한 번뿐이라는 규칙은 방장에게는 적용되지 않아요.',
          '생방송에 잘 맞는 데는 분명한 이유가 있어요. 방장이 그리지 않으니, 시청자는 방장의 숨은 자리가 만들어지는 과정을 전혀 보지 못해요. 다른 모드에서는 방장이 자기 영역을 칠하는 장면이 그대로 나가서, 시청자에게 찾을 재미가 남지 않아요.',
          '권장: 4명에서 10명. 무료이고, 앞으로도 무료예요.',
        ],
      },
      {
        heading: '각자 한 장',
        blocks: [
          '플레이어마다 작품 한 장을 받아 그 안에 자기 수정을 숨겨요. 찾을 차례가 되면 다른 한 사람의 그림만 건네받고, 다시 그려진 영역을 단 한 번의 클릭으로 짚어야 해요. 경쟁도 뺏기도 없어요. 나와 그림 한 장, 그리고 결정 하나뿐이에요.',
          '둘이 할 때, 또는 각자 여유 있게 진행하는 조용한 판을 원할 때 좋아요.',
          '권장: 2명에서 10명.',
        ],
      },
      {
        heading: '한 명만 그리기',
        blocks: [
          '한 라운드에 한 명이 그리고, 나머지 전원이 뒤를 쫓아요.',
          '라운드마다 그림을 받는 사람은 한 명이에요. 나머지는 기다렸다가 그 사람의 수정을 찾고, 각자 자기 시간으로 점수를 받아요. 그린 사람은 가장 빨리 찾은 사람을 기준으로 점수를 받고, 남들이 찾는 동안 멈춰 있는 자기 숨은 자리를 다시 봐요. 역할은 돌아가요. 여기서는 라운드 설정이 한 바퀴를 세요. 모두가 같은 횟수만큼 그려야 공평하니까요.',
          '같은 조건에서 한 장씩 실력을 겨루고 싶은 적은 인원에게 좋아요.',
          '권장: 3명에서 6명. 그보다 많아지면 기다리는 시간이 길어져요.',
        ],
      },
      {
        heading: '배틀로얄',
        blocks: [
          '다 같이 찾기에, 라운드마다 한 명의 탈락을 더한 모드예요.',
          '같은 그림 한 장, 같은 동시 탐색이에요. 다만 여기서는 점수를 한 점도 세지 않아요. 물음은 누가 남느냐, 그것뿐이에요. 수정이 가장 먼저 들킨 사람이 판을 떠나요. 나머지는 라운드가 끝날 때까지 계속 찾아요. 더는 아무도 탈락시키지 않지만, 각자에게 그 라운드의 순위가 붙어서 자기 영역 위에 표시돼요. 아무도 찾히지 않으면 아무도 탈락하지 않아요.',
          '마무리로 좋아요. 자리가 비어 갈수록 긴장이 올라가고, 마지막 라운드는 둘이서 하게 돼요.',
          '권장: 4명에서 10명.',
        ],
      },
      {
        heading: '콘테스트',
        blocks: [
          '아무도 찾지 않는 유일한 모드예요.',
          '모두가 같은 그림의 같은 영역을 다시 그려요. 그다음 각자 자기 속도로 다른 사람들의 수정을 둘러보고, 가장 잘됐다고 생각하는 것에 투표해요. 받은 표 하나가 그 주인에게 1점이 돼요. 투표하는 동안 누가 그렸는지는 가려지고, 자기 것에는 투표할 수 없어요. 이름과 표는 공개 때 비로소 나와요.',
          '여러 사람이 섞여 있을 때, 또는 찾는 시간에 쫓기지 않고 그리고 싶은 사람이 있을 때 좋아요.',
          '권장: 3명에서 10명.',
        ],
      },
    ],
  },
  modeTable: [
    { name: '다 같이 찾기', players: '3명에서 10명', duration: '15~20분' },
    { name: '스트리밍', players: '4명에서 10명', duration: '15~20분' },
    { name: '각자 한 장', players: '2명에서 10명', duration: '15~20분' },
    { name: '한 명만 그리기', players: '3명에서 6명', duration: '20~35분' },
    { name: '배틀로얄', players: '4명에서 10명', duration: '20~30분' },
    { name: '콘테스트', players: '3명에서 10명', duration: '10~15분' },
  ],
  modeTableHeadings: ['모드', '인원', '소요 시간'],
  premium: {
    heading: '무료 모드와 유료 모드',
    blocks: [
      '다 같이 찾기와 스트리밍 모드는 앞으로도 계속 무료예요.',
      '나머지 넷은 판을 「만들 때」만 유료가 될 예정이에요. 참가는 언제까지나 무료예요. 즉 자리에 한 사람만 잠금을 풀어 두면, 모두가 모든 모드로 놀 수 있어요. 계정도 결제도 필요 없어요.',
      '잠금 해제는 한 번만 결제하는 방식이고 구독은 없어요. 결제한 계정에서는 광고도 사라져요. 그때까지는 모든 모드가 누구에게나 열려 있어요.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'Retusch가 뭔가요?',
      date: '2026-08-21',
      summary:
        '여러 명이 함께하는 온라인 그림 게임이에요. 형식은 Gartic Phone에 가깝지만, 작동 원리는 오히려 Meccha Chameleon을 닮았어요. 알아보게 만드는 대신 사라지려고 애쓰거든요.',
      sections: [
        {
          heading: '들키면 지는 그림 게임',
          blocks: [
            'Retusch는 2명에서 10명이 함께하는 온라인 그림 게임이에요. 라운드마다 한 영역이 지워진 그림을 받고, 그 영역을 작품에 녹아들도록 다시 그려야 해요. 그다음 다 같이 수정된 그림들을 보면서, 다시 그려진 영역을 찾아요.',
            '무엇이 다른지는 한 문장이면 돼요. 이기는 건 들키지 않는 거예요.',
          ],
        },
        {
          heading: 'Gartic Phone과 닮은 점',
          blocks: [
            '형식은 똑같아요. 링크를 열고, 이름을 적고, 놀아요. 계정도, 설치도, 내려받을 프로그램도 없어요. 브라우저에서 돌아가고 한 판이 몇 초 만에 시작돼요. 친구를 부르는 것도 대화창에 링크를 붙이면 끝이고, 자리가 금세 차요.',
            '흐름도 익숙해요. 짧은 라운드, 타이머, 그리는 시간, 그리고 남들이 뭘 만들었는지 드러나는 시간. 무엇보다 라운드 끝에 다 같이 보면서 한마디씩 하는 그 순간이 그대로예요. 함께 웃는 재미가 같아요.',
            'Gartic Phone처럼 그림을 잘 그릴 필요는 없어요. 자리를 잘 잡은 하늘 한 조각이, 삐져나온 대작을 이겨요.',
          ],
        },
        {
          heading: 'Meccha Chameleon에서 가져온 점',
          blocks: [
            '다만 뿌리에 있는 원리는 다른 데 있어요. Steam의 3D 게임 Meccha Chameleon에서는 자기 캐릭터를 직접 칠한 뒤 배경 속에 놓고, 아무도 주변과 구분하지 못하기를 바라요. 승패를 가르는 건 닮게 만드는 솜씨와 놓는 자리이지, 조작 실력이 아니에요.',
            'Retusch는 그 발상을 그림의 세계로 되가져왔어요. 숨을 자리는 고를 수 없고 영역은 게임이 정해 줘요. 대신 그 안에 들어갈 것은 전부 직접 만들어요. 시선이 멈추지 않고 미끄러져 지나가면, 그 숨은 자리는 성공이에요.',
            '그리고 위장 게임이 늘 그렇듯 긴장은 두 역할에서 나와요. 숨는 사람이면서 동시에 찾는 사람이니까요. 수상한 면을 찾아 그림을 노려보는 동안, 누군가는 당신 것을 노려보고 있어요.',
          ],
        },
        {
          heading: '다른 데는 없는 점',
          blocks: [
            '점수는 양쪽을 정반대로 보상해요. 찾는 쪽은 빨리 찾을수록 많이 받고, 그린 쪽은 자기 영역이 오래 버틸수록 많이 받아요. 아무도 알아채지 못한 수정은 주인에게 최고점을 줘요.',
            '여기서 아주 독특한 태도가 생겨요. 예쁘게 그리려는 게 아니라 정확하게 그리려는 태도예요. 맞는 색을 집고, 선을 제자리에서 이어 주고, 어느 모서리도 비워 두지 않기. 그리는 동작이기 전에 보는 동작이에요.',
            '직접 겪어 보는 게 가장 빨라요. 전체 규칙을 읽거나, 바로 시작을 눌러 보세요. 설정도 기다림도 없이 시작돼요.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Retusch에서 무적이 되는 법',
      date: '2026-08-21',
      summary:
        '단축키, 큰 것에서 작은 것으로 가는 순서, 그리고 모서리를 비워 두는 실수. 초보와, 이제 아무도 못 찾는 사람을 가르는 세 가지.',
      sections: [
        {
          heading: '단축키를 익히면 쓸 시간이 두 배가 돼요',
          blocks: [
            '도구를 매번 마우스로 집으러 가면 2분은 순식간에 사라져요. 단축키는 라운드마다 20초에서 30초를 쉽게 되찾아 주는데, 그게 바로 세부에 쓸 시간이에요.',
            {
              list: [
                'B는 붓, P는 채우기, I는 스포이트, H는 손이에요.',
                '1, 2, 3은 붓 굵기 세 가지예요. 도구 줄까지 갈 필요가 없어요.',
                'Ctrl+Z, 맥에서는 Cmd+Z로 획 하나씩 되돌려요.',
                'Alt를 누르고 있으면 스포이트가 되고, 떼는 순간 원래 도구로 돌아와요. 이게 가장 쓸모 있어요. 그리던 중에도 도구를 바꾸지 않고 색을 집을 수 있거든요.',
              ],
            },
            'AZERTY 자판을 쓴다면 한 가지 알아 두세요. 자판에 &, é, "가 적혀 있어도 1, 2, 3은 Shift 없이 작동해요. 게임은 키가 놓인 자리를 읽지, 찍히는 문자를 읽지 않아요.',
          ],
        },
        {
          heading: '큰 것에서 작은 것으로 칠하세요',
          blocks: [
            '초보가 흔히 하는 실수는 한쪽 구석부터 시작해 거기를 공들여 다듬는 거예요. 시간이 끝나면 영역의 4분의 3이 아직 바탕색이고, 수정은 10미터 밖에서도 보여요.',
            '올바른 순서는 그 반대예요. 굵은 붓이나 채우기로 시작해 큰 색 덩어리를 놓으세요. 하늘, 벽, 바닥의 그림자. 20초면 영역 전체의 인상이 맞아떨어지고, 더는 이질적인 덩어리로 읽히지 않아요.',
            '그다음에야 굵기를 줄이고 경계를 다뤄요. 가로지르는 지평선, 물체의 가장자리, 중간 높이에서 바뀌는 그러데이션. 시간이 모자라도 잃는 건 세부이지 라운드가 아니에요.',
            '색은 팔레트에서 고르지 말고 언제나 스포이트로 집으세요. 어림잡은 색조 하나로 영역 전체가 들통나지만, 정확한 색조는 그것을 보이지 않게 만들어요.',
          ],
        },
        {
          heading: '모서리는 절대 비워 두지 마세요',
          blocks: [
            '이게 첫 번째 단서이고, 익숙한 사람이 가장 먼저 살피는 곳이에요. 가운데는 잘 다듬었는데 네 모서리가 바탕색 그대로면 화면에 완벽한 직사각형이 그려져요. 그림을 볼 필요도 없어요. 도형 모양이 눈에 확 들어와요.',
            '가장자리를 먼저, 가운데를 나중에 다루는 습관을 들이세요. 잘 섞인 테두리는 대충 그린 속을 덮어 주지만, 그 반대는 결코 통하지 않아요.',
            '가장자리는 일부러 넘겨서 칠하세요. 획은 영역 경계에서 잘리니까 훨씬 바깥까지 나가도 잃을 게 없고, 모서리에 바탕 점 하나 남지 않았다고 확신할 수 있는 유일한 방법이에요.',
          ],
        },
        {
          heading: '찾을 차례가 오면',
          blocks: [
            '먼저 확대하지 말고 그림 전체를 훑으세요. 어색하게 섞인 영역은 얼룩처럼 보이고, 확대는 확인할 때만 쓰면 돼요. 그다음 긴 선, 화면을 가로지르는 선을 따라가 보세요. 머리카락만큼 어긋난 가장자리는 거의 언제나 수정이에요.',
            '그리고 한 라운드에 실수는 한 번뿐이라는 걸 기억하세요. 3초짜리 확인이 라운드 하나보다 쌉니다.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: '친구들과 하는 그림 게임',
      date: '2026-08-21',
      summary:
        'Retusch가 적은 인원에서 잘 굴러가는 이유, 링크만 붙여 초대하는 법, 그리고 한 사람만 있으면 자리 전체에 유료 모드가 열리는 구조.',
      sections: [
        {
          heading: '아무에게도 아무것도 요구하지 않는 게임',
          blocks: [
            '멀리 떨어져 게임하는 밤의 가장 큰 걸림돌은 준비예요. 만들어야 하는 계정, 내려받아야 하는 프로그램, 맞지 않는 버전. 시작도 하기 전에 절반이 포기해요.',
            'Retusch는 브라우저에서 돌아가요. 친구들은 링크를 누르고 이름을 적으면 이미 자리에 앉아 있어요. 설치할 것도 만들 것도 없고, 컴퓨터에서도 휴대폰에서도 잘 돌아가요.',
            '여럿이 놀 때 중요한 점이 하나 더 있어요. 그림을 못 그린다고 빠지는 사람이 생기지 않아요. 이 게임은 무언가를 그려 내라고 하지 않고, 한 영역을 주변에 맞추라고만 해요. 그림은 서툴러도 잘 보는 사람이 자주 이겨요.',
          ],
        },
        {
          heading: '초대하는 방법',
          blocks: [
            '첫 화면에서 방을 만드세요. 우리끼리만 하고 싶다면 ‘공개 방’ 표시를 꺼 두세요. 그러면 링크 초대가 유일한 입구가 돼요.',
            '전용 버튼으로 링크를 복사해서 단체 대화방이나 음성 채널, 메신저에 붙여 넣으세요. 방 코드가 들어 있어서 친구들이 곧바로 당신 자리로 내려와요.',
            '사람들이 모이는 동안 설정을 만질 수 있어요. 모드, 라운드 수, 그리는 시간, 영역 크기, 심지어 그림 카테고리까지요. 시작을 누르기 전까지는 아무것도 확정되지 않아요.',
          ],
        },
        {
          heading: '한 명이 결제하면 모두가 놀아요',
          blocks: [
            '두 모드는 영원히 무료예요. 기본인 다 같이 찾기, 그리고 스트리밍이에요.',
            '나머지는 유료가 되지만, 판을 「만들 때」만 그래요. 참가는 조건 없이 무료로 남아요. 실제로는 무리 중 한 사람만 잠금을 풀어 두면 자리 전체가 모든 모드를 누려요. 계정도, 한 푼의 결제도 필요 없어요.',
            '잠금 해제는 한 번만 결제하는 방식이고 구독은 없어요.',
          ],
        },
        {
          heading: '인원에 맞는 모드',
          blocks: [
            '셋이나 넷이면 다 같이 찾기로 충분하고, 한 라운드면 이해돼요. 여섯 명을 넘으면 진가가 나와요. 발견이 잇따르고 화면이 쉬지 않고 움직여요.',
            '마무리로는 배틀로얄을 해 보세요. 라운드마다 자리가 비고 마지막은 둘이서 하게 돼요. 여러 사람이 섞여 있고 그중에 느긋하게 그리고 싶은 사람이 있다면, 콘테스트가 찾는 시간의 압박을 통째로 걷어 내 줘요.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: '방송에서 하는 그림 게임',
      date: '2026-08-21',
      summary:
        'Retusch에는 생방송을 위해 만든 모드가 있어요. 스트리머가 혼자 찾고 시청자들이 숨어요. 그 모드를 쓰는 법과, 시청자 앞에서 통하는 다른 모드들.',
      sections: [
        {
          heading: '그러라고 만든 모드',
          blocks: [
            '스트리밍 모드는 역할을 뒤집어요. 방장을 뺀 모든 사람이 같은 그림에 그리고, 방장은 전혀 그리지 않아요. 그다음 찾는 사람은 방장뿐이고, 시청자들은 지켜봐요.',
            '생방송에 꼭 필요한 상황이 바로 이거고, 이유도 분명해요. 방장이 그리지 않으니 방장의 숨은 자리는 아예 없고, 채팅은 보지 말아야 할 것을 하나도 보지 않았어요. 반대로 시청자는 다른 플레이어들의 숨은 자리를 알고 있어서, 방장이 그 옆을 세 번 연달아 지나치는 걸 구경하게 돼요.',
            '이 상황을 위해 규칙 두 개가 쓰였어요. 스트리머는 점수를 받지 않고 순위에도 오르지 않아요. 승부는 숨는 사람들끼리 가리고, 스트리머는 판을 이끌어요. 그리고 몇 번을 틀려도 괜찮아요. 다른 모드는 첫 실수에서 멈추지만, 생방송이 운 나쁜 클릭 하나로 끝나 버릴 수는 없으니까요.',
            '스트리밍 모드는 무료이고, 앞으로도 무료예요.',
          ],
        },
        {
          heading: '콘테스트도 잘 맞아요',
          blocks: [
            '콘테스트에서는 모두가 같은 그림의 같은 영역을 다시 그리고, 각자 가장 마음에 드는 수정에 투표해요. 더 찾을 것은 없고, 고르는 일만 남아요.',
            '시청자 앞에서 아주 잘 굴러가는데, 이유는 스트리밍 모드와 같아요. 찾을 것이 없으니 채팅이 망칠 것도 없어요. 작품을 나란히 놓고 견주고, 투표가 긴장의 순간을 만들고, 이름 공개가 깔끔한 마무리가 돼요. 가장 짧은 모드이기도 해요.',
          ],
        },
        {
          heading: '다른 모드들은',
          blocks: [
            '꼭 전용 모드여야 하는 건 아니지만, 그럴 때는 채팅이 아니라 친구들과 하는 편이 좋아요. 다른 모든 모드에서는 스트리머가 자기 영역을 화면에서 칠하니까, 시청자는 그게 만들어지는 걸 보고 찾기가 시작되기도 전에 답을 알아 버려요.',
            '반대로 친구들끼리 하는 판은 밖에서 보기에 아주 좋아요. 그리는 시간에는 저마다 숨은 자리를 짓는 모습이 보이고, 공개 때는 모든 영역이 주인 이름과 함께 한꺼번에 열려요.',
          ],
        },
        {
          heading: '채팅을 판에 끌어들이기',
          blocks: [
            '공유할 것은 초대 링크 하나뿐이에요. 채팅에 붙여 넣으면 먼저 온 사람들이 자리를 채워요. 만들 계정도, 내려받기도, 대기열도 없어요.',
            '인원 상한은 방에서 2명에서 10명 사이로 정해요. 시청자가 많을 때는 긴 판 하나보다 짧은 판을 여러 개 여는 편이 나아요. 한 라운드는 3분도 걸리지 않아요.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Retusch의 새 소식',
      date: '2026-08-21',
      summary:
        'Retusch에 들어간 게임 쪽 변경 사항을 최신순으로 정리했어요. 새 모드, 새 규칙, 그리기와 찾기의 개선.',
      sections: [
        {
          heading: '2026년 8월',
          blocks: [
            {
              list: [
                '다시 그릴 영역이 흰 사각형에서 시작하지 않고, 그 아래에 가장 많이 깔린 색에서 시작해요. 그림에 있지도 않은 바탕을 다시 칠하느라 시간의 절반을 쓰지 않아도 돼요.',
                '채우기가 획 옆에 밝은 테두리를 남기지 않아요. 이제는 부드럽게 처리된 획의 가장자리 밑으로 파고들어요.',
                '배틀로얄이 점수를 세지 않아요. 가장 먼저 들킨 사람이 판을 떠나고, 각자에게 그 라운드의 순위가 빨강에서 초록으로 자기 영역 위에 표시돼요.',
                '스트리밍 모드에서는 스트리머가 원하는 만큼 시도할 수 있어요. 첫 실수로 라운드가 끝나지 않아요.',
                '관전 중에 상대가 그리고 있는 그림을 옮기고 확대할 수 있게 됐어요.',
                '이모지 반응이 화면 아래가 아니라, 플레이어 목록의 보낸 사람 이름에서 출발해요.',
                '다시 그릴 영역 바깥에서는 붓과 채우기로도 화면을 옮길 수 있어요. 움직이려고 손 도구를 꺼낼 필요가 없어요.',
                '게임 중 왼쪽 위의 게임 이름을 누르면 확인을 거쳐 첫 화면으로 돌아가요.',
                '일본어와 한국어가 더해져 지원 언어가 17개가 됐어요.',
              ],
            },
          ],
        },
        {
          heading: '2026년 7월',
          blocks: [
            {
              list: [
                '콘테스트 모드가 생겼어요. 모두가 같은 영역을 다시 그리고, 각자 가장 마음에 드는 수정에 투표해요.',
                '생방송을 위한 스트리밍 모드가 생겼어요. 방장이 혼자 찾고 시청자들이 숨어요.',
                '난이도 대신 그림 카테고리가 들어왔어요. 보고 싶은 것을 골라 두면 되고, 카테고리마다 예시 그림을 보여 줘요.',
                '두 라운드 연속으로 같은 그림이 나오지 않고, 고른 카테고리가 라운드마다 번갈아 나와요.',
                '판이 끝난 뒤 갤러리에서 수정을 획 단위로 다시 볼 수 있고, 이미지나 움직이는 GIF로 내보낼 수 있어요.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: '한눈에',
    allArticles: '모든 글',
    theRules: '게임 규칙',
    theModes: '게임 모드',
  },
};
